/*
  MicPicker — lets the therapist explicitly choose which microphone
  to use for a SOAP recording, with a live level meter so they can
  confirm audio is coming in BEFORE they hit Start.

  The chosen deviceId is persisted in localStorage under
  `soap_mic_device_id` and read by recording.jsx during startRecording().

  Why this exists: without an explicit deviceId, getUserMedia() picks
  the browser/OS default, which does NOT update when a therapist plugs
  in a USB or Bluetooth mic mid-session.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Mic, MicOff, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const LS_KEY = "soap_mic_device_id";

export function getSavedMicDeviceId() {
  try { return localStorage.getItem(LS_KEY) || ""; } catch { return ""; }
}

export function saveMicDeviceId(id) {
  try {
    if (id) localStorage.setItem(LS_KEY, id);
    else localStorage.removeItem(LS_KEY);
  } catch { /* ignore */ }
}

export default function MicPicker({ disabled }) {
  const [devices, setDevices] = useState([]);
  const [selected, setSelected] = useState(getSavedMicDeviceId());
  const [labelsUnlocked, setLabelsUnlocked] = useState(false);
  const [testing, setTesting] = useState(false);
  const [level, setLevel] = useState(0);          // 0..1 RMS
  const [peak, setPeak] = useState(0);            // rolling peak
  const [enumErr, setEnumErr] = useState("");

  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const peakRef = useRef(0);

  const loadDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setEnumErr("This browser does not support device enumeration.");
      return;
    }
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      const mics = list.filter((d) => d.kind === "audioinput");
      setDevices(mics);
      // If any device has a label, permission has been granted at least once.
      setLabelsUnlocked(mics.some((d) => d.label));
      // If our saved device is no longer present, clear it so we don't
      // keep pinning to a mic that was unplugged.
      const savedId = getSavedMicDeviceId();
      if (savedId && !mics.some((d) => d.deviceId === savedId)) {
        saveMicDeviceId("");
        setSelected("");
        toast.warning("Saved microphone is unplugged", {
          description: "Falling back to the system default. Pick a new mic and test it.",
        });
      }
    } catch (e) {
      setEnumErr(e?.message || "Could not list microphones");
    }
  }, []);

  useEffect(() => {
    loadDevices();
    if (!navigator.mediaDevices?.addEventListener) return;
    const handler = () => loadDevices();
    navigator.mediaDevices.addEventListener("devicechange", handler);
    return () => navigator.mediaDevices.removeEventListener("devicechange", handler);
  }, [loadDevices]);

  const stopTest = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    streamRef.current = null;
    try { audioCtxRef.current?.close(); } catch { /* ignore */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
    peakRef.current = 0;
    setLevel(0);
    setPeak(0);
    setTesting(false);
  }, []);

  useEffect(() => () => stopTest(), [stopTest]);

  const startTest = useCallback(async () => {
    if (testing) { stopTest(); return; }
    try {
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
          ...(selected ? { deviceId: { exact: selected } } : {}),
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      // Refresh device list — labels are now populated.
      loadDevices();
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyserRef.current = analyser;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        // Convert 0..255 samples to signed -1..1, then RMS.
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        // Soft-clip so a full-scale sine reads ~1.0 not 0.7
        const norm = Math.min(1, rms * 1.6);
        setLevel(norm);
        peakRef.current = Math.max(peakRef.current * 0.985, norm);
        setPeak(peakRef.current);
        rafRef.current = requestAnimationFrame(tick);
      };
      setTesting(true);
      tick();
    } catch (e) {
      const msg = e?.message || String(e);
      toast.error("Could not open microphone", {
        description: e?.name === "NotAllowedError"
          ? "Permission was denied. Check the browser's mic permission for this site."
          : msg,
      });
      stopTest();
    }
  }, [selected, testing, stopTest, loadDevices]);

  function onSelect(e) {
    const id = e.target.value;
    setSelected(id);
    saveMicDeviceId(id);
    // If we're currently testing, restart the stream on the new device.
    if (testing) {
      stopTest();
      // Small delay so the previous stream releases cleanly.
      setTimeout(() => startTest(), 100);
    }
  }

  const currentDevice = devices.find((d) => d.deviceId === selected);
  const barPct = Math.round(level * 100);
  const peakPct = Math.round(peak * 100);
  const silent = testing && peak < 0.02;

  return (
    <div className="rounded-md border border-zinc-200 p-3 space-y-2" data-testid="mic-picker">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs flex items-center gap-1.5">
          <Mic className="h-3.5 w-3.5" /> Microphone
        </Label>
        <button
          type="button"
          onClick={loadDevices}
          className="text-2xs text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1"
          data-testid="mic-picker-refresh"
          title="Refresh device list"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <div className="flex gap-2">
        <select
          value={selected}
          onChange={onSelect}
          disabled={disabled}
          className="h-9 flex-1 rounded-md border border-zinc-200 bg-white px-2 text-sm disabled:bg-zinc-50 disabled:text-zinc-500"
          data-testid="mic-picker-select"
        >
          <option value="">System default</option>
          {devices.map((d, idx) => (
            <option key={d.deviceId || idx} value={d.deviceId}>
              {d.label || `Microphone ${idx + 1} (unlock name by testing)`}
            </option>
          ))}
        </select>
        <Button
          type="button"
          size="sm"
          variant={testing ? "secondary" : "outline"}
          onClick={startTest}
          disabled={disabled}
          data-testid="mic-picker-test-btn"
          className="h-9"
        >
          {testing
            ? <><MicOff className="h-3.5 w-3.5 mr-1" /> Stop test</>
            : <><Mic className="h-3.5 w-3.5 mr-1" /> Test mic</>}
        </Button>
      </div>

      {/* Level meter */}
      <div className="space-y-1">
        <div className="h-2.5 w-full bg-zinc-100 rounded overflow-hidden relative" data-testid="mic-picker-meter">
          <div
            className={`h-full transition-[width] duration-75 ${
              level > 0.85 ? "bg-red-500"
                : level > 0.5 ? "bg-emerald-500"
                : level > 0.05 ? "bg-emerald-400"
                : "bg-zinc-300"
            }`}
            style={{ width: `${barPct}%` }}
          />
          {testing && peak > 0 && (
            <div
              className="absolute top-0 h-full w-[2px] bg-zinc-700/70"
              style={{ left: `${peakPct}%` }}
            />
          )}
        </div>
        <div className="text-2xs text-zinc-500 flex items-center justify-between">
          <span>
            {testing ? (
              silent
                ? <span className="text-amber-700">No sound detected — speak or tap the mic.</span>
                : <span className="text-emerald-700">Receiving audio · peak {peakPct}%</span>
            ) : currentDevice?.label
              ? <>Selected: <span className="font-medium">{currentDevice.label}</span></>
              : !labelsUnlocked
                ? "Tap Test mic to see real device names (browser hides labels until permission is granted)."
                : "System default microphone."}
          </span>
        </div>
      </div>

      {enumErr && (
        <div className="text-2xs text-red-600">{enumErr}</div>
      )}
    </div>
  );
}
